import { onUnmounted, ref, type Ref } from 'vue';

const DEFAULT_HOLD_DURATION_MS = 4000;
const DEFAULT_REQUIRED_FINGERS = 2;

function countTouchesOnElement(element: HTMLElement, touches: TouchList): number {
  const rect = element.getBoundingClientRect();
  let count = 0;
  for (const touch of Array.from(touches)) {
    if (
      touch.clientX >= rect.left &&
      touch.clientX <= rect.right &&
      touch.clientY >= rect.top &&
      touch.clientY <= rect.bottom
    ) {
      count += 1;
    }
  }
  return count;
}

export function useTwoFingerLongPress(
  targetRef: Ref<HTMLElement | null>,
  onComplete: () => void,
  options?: { holdDurationMs?: number; requiredFingers?: number },
) {
  const holdDurationMs = options?.holdDurationMs ?? DEFAULT_HOLD_DURATION_MS;
  const requiredFingers = options?.requiredFingers ?? DEFAULT_REQUIRED_FINGERS;
  const progress = ref(0);

  let startTime: number | null = null;
  let rafId: number | null = null;

  function reset() {
    startTime = null;
    progress.value = 0;
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  }

  function hasEnoughFingersOnTarget(touches: TouchList): boolean {
    const element = targetRef.value;
    if (!element) return false;
    return countTouchesOnElement(element, touches) >= requiredFingers;
  }

  function tick() {
    if (startTime === null) return;
    const elapsed = Date.now() - startTime;
    progress.value = Math.min(elapsed / holdDurationMs, 1);
    if (progress.value >= 1) {
      reset();
      onComplete();
      return;
    }
    rafId = requestAnimationFrame(tick);
  }

  function handleTouchStart(event: TouchEvent) {
    if (!hasEnoughFingersOnTarget(event.touches)) {
      if (startTime !== null) reset();
      return;
    }
    if (startTime === null) {
      startTime = Date.now();
      rafId = requestAnimationFrame(tick);
    }
  }

  function handleTouchMove(event: TouchEvent) {
    if (startTime === null) return;
    if (!hasEnoughFingersOnTarget(event.touches)) {
      reset();
    }
  }

  function handleTouchEnd(event: TouchEvent) {
    if (startTime === null) return;
    if (!hasEnoughFingersOnTarget(event.touches)) {
      reset();
    }
  }

  onUnmounted(reset);

  return { handleTouchStart, handleTouchMove, handleTouchEnd };
}
